import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import syncService from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './shared/Spinner';
import ReportAnnotation from './shared/ReportAnnotation';
import ReportActions, { htmlCollapsible } from './shared/ReportActions';
import './SyncReportPage.css';

// Comment markers for PROCESSED records that still need attention or are worth noting.
const WARNING_MARKERS = [
    { phrase: 'not assigned in OHIP', severity: 'warning', key: 'room-not-assigned', label: 'Room not assigned in OHIP',
      guidance: 'The reservation synced, but OHIP would not accept the pre-assigned room (occupied / max occupancy). Front office to assign a room at the property.' },
    { phrase: 'Rate-inclusive package', severity: 'info', key: 'rate-inclusive-package', label: 'Rate-inclusive package via rate plan',
      guidance: 'Informational: the package is applied automatically by the rate plan, not as an add-on. No action needed.' },
    { phrase: 'already shared in OHIP', severity: 'info', key: 'already-shared', label: 'Already shared in OHIP',
      guidance: 'Informational: the share link already exists in OHIP. No action needed.' },
    { phrase: 'Negotiated rate already attached', severity: 'info', key: 'neg-rate-exists', label: 'Negotiated rate already attached',
      guidance: 'Informational: the negotiated rate already exists on the profile in OHIP. No action needed.' },
    { phrase: 'nothing to cancel', severity: 'info', key: 'cancel-noop', label: 'Cancelled in source, never in OHIP',
      guidance: 'Informational: the reservation was cancelled in the source before it ever synced to OHIP. Nothing to cancel.' },
    // Matches both "Accompanying profile <id> removed…" and the plural "Accompanying profiles <id>, <id> removed…"
    // — the comment filter is a match_phrase on the analyzed comment, so the phrase must be common to both.
    { phrase: 'removed from reservation', severity: 'info', key: 'accompanying-guest-removed', label: 'Accompanying guest removed',
      guidance: 'Informational: the target connector has REMOVE_ACCOMPANYING_GUEST set to Y, so only the primary guest was sent. The comment names the source profile ids that were left off the reservation. No action needed.' },
];

function lastHttpFailure(comment) {
    const re = /(GET|PUT|POST|PATCH|DELETE)\s+(\S+)\s+→\s+(\d{3})/g;
    let m, last = null;
    while ((m = re.exec(comment)) !== null) {
        const status = parseInt(m[3], 10);
        if (status >= 400 && status !== 404) last = { method: m[1], path: m[2], status };
    }
    return last;
}
function endpointTag(path) {
    const clean = path.split('?')[0].replace(/\/$/, '');
    const segs = clean.split('/').filter(s => s && !/^\d+$/.test(s));
    return segs.length ? segs[segs.length - 1] : clean;
}

// Classify an ERROR record into an actionable group from its comment.
function classifyError(comment) {
    const c = comment || '';
    if (c.includes('No inventory is booked for the block'))
        return { severity: 'error', key: 'block-inventory', label: 'Block has no inventory in OHIP',
            guidance: 'The block exists in OHIP but has no room inventory (allotment) booked. Stock the block allotment in OHIP, then reprocess these reservations.' };
    if (c.includes('Missing linked Profile'))
        return { severity: 'error', key: 'missing-profile', label: 'Missing linked profile',
            guidance: 'A linked profile (guest / company / group) is not yet in OHIP. Request the profile first, then reprocess the reservation.' };
    if (/RSV01320/.test(c) || /Transaction code is not configured/i.test(c))
        return { severity: 'error', key: 'config-txn-code', label: 'OHIP config gap — transaction code',
            guidance: 'A transaction / rate code referenced by the reservation is not configured in OHIP. Configure it in OHIP, then reprocess.' };
    if (/OPERAWS-GEN01278/.test(c) || /Please enter a valid number/i.test(c))
        return { severity: 'error', key: 'invalid-number', label: 'Invalid / missing number on create',
            guidance: 'A required numeric value was missing on the create payload. Needs investigation of the source record.' };
    const fail = lastHttpFailure(c);
    if (fail) {
        const tag = endpointTag(fail.path);
        return { severity: 'error', key: `http-${fail.method}-${tag}-${fail.status}`,
            label: `${fail.method} …/${tag} → ${fail.status}`,
            guidance: 'OHIP rejected this call. Review the record’s message log for detail.' };
    }
    return { severity: 'error', key: 'other-error', label: 'Other error',
        guidance: 'Review the record’s message log for detail.' };
}

// Guidance for the rejection codes that produce a WARNING record. A WARNING means the target ACCEPTED
// the write and refused one part of it, so the guidance is never "reprocess this" — it is "fix the
// configuration"; the already-synced record does not need re-sending, and the next update will carry
// the missing piece once the mapping exists.
const WARNING_CODE_GUIDANCE = {
    CRM11012: {
        label: 'Preference not mapped in OHIP',
        guidance: 'The profile synced, but OHIP refused one preference because its type/value has no entry in the target’s Data Value Mappings. Add the mapping in OHIP (superadmins can use the DVM button on the record’s message log), then re-send the profile only if the preference matters on the existing record — nothing else was lost.',
    },
};

// Classify a WARNING record from the rejection code in its comment. oracle-ohip writes the codes into
// the comment as "Accepted by OHIP with rejection(s): CRM11012 Invalid Preference Type Combination …",
// so the group is the code — which is what the person fixing it acts on.
function classifyWarning(comment) {
    const c = comment || '';
    const m = c.match(/\b([A-Z]{2,}[0-9]{4,})\b/);
    const code = m ? m[1] : null;
    const known = code && WARNING_CODE_GUIDANCE[code];
    if (known) return { severity: 'warning', key: `warn-${code}`, label: `${known.label} (${code})`, guidance: known.guidance };
    if (code) {
        return { severity: 'warning', key: `warn-${code}`, label: `Partly rejected by OHIP (${code})`,
            guidance: 'The record synced, but OHIP rejected part of it. Open the record’s message log to see which call was refused and why. No reprocess is needed for the parts that were accepted.' };
    }
    return { severity: 'warning', key: 'warn-other', label: 'Partly rejected by OHIP',
        guidance: 'The record synced, but OHIP rejected part of it. Review the record’s message log for detail.' };
}

// Every record matching a filter — the whole task, not its newest 1,000 (see searchAllReconciliationRecords).
const fetchAll = (syncId, taskId, filters) => syncService.searchAllReconciliationRecords(syncId, taskId, filters);

// Annotated SPR status-history entries: [note] (manual comment), [dvm] (a mapping the DVM tool
// created), [cc] (a payment token that could not be swapped — the card was removed and the reservation
// sent as cash). Pulled out of the record's status history (ignoring pipeline status-change entries).
const OPERATOR_NOTE_RE = /^\s*\[(note|dvm|cc)\]\s*/i;
function notesFor(r) {
    const hist = Array.isArray(r.statusHistory) ? r.statusHistory : [];
    const notes = hist
        .map(h => (h && h.comment) ? String(h.comment) : '')
        .filter(c => OPERATOR_NOTE_RE.test(c))
        .map(c => c.replace(/\s+/g, ' ').trim());
    // [cc] is an automated per-sync status — we only want the LATEST. [note]/[dvm] are distinct operator
    // notes — keep them all.
    const isCc = (n) => /^\[cc\]/i.test(n);
    const lastCc = notes.map(isCc).lastIndexOf(true);
    return notes.filter((n, i) => !isCc(n) || i === lastCc);
}

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function SyncReportPage() {
    const params = useParams();
    const syncId = params.syncId || params.syncId;
    // Same tolerance as the project id on the line above: the routes address a task by its id now,
    // and older links still carry the uuid.
    const taskId = params.taskId || params.taskId;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isSuperAdmin } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState([]);      // getTaskSummary rows (counts over ALL records)
    const [problemRecords, setProblemRecords] = useState([]); // errors + flagged warnings (detail)
    const [projectName, setProjectName] = useState('');
    const [taskName, setTaskName] = useState('');
    const [generatedAt, setGeneratedAt] = useState(null);
    const scopeType = searchParams.get('scopeType') || '';

    // Per-group operator annotations: { [groupKey]: text }. Edited inline; persisted per (task, group).
    const [annotations, setAnnotations] = useState({});

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!isSuperAdmin) { setLoading(false); return; }
            try {
                setLoading(true);
                setError(null);
                try {
                    const proj = await syncService.getSyncProjectDetails(syncId);
                    if (!cancelled) setProjectName(proj?.projectDetails?.projectName || proj?.projectName || proj?.name || '');
                    const task = await syncService.getTaskDetails(syncId, taskId);
                    if (!cancelled) setTaskName(task?.taskDetails?.taskName || task?.taskName || task?.name || '');
                } catch (_) { /* names best-effort */ }

                // Counts over ALL records come from the summary endpoint (not a capped record fetch).
                const summaryData = await syncService.getTaskSummary(syncId, taskId);
                if (!cancelled) setSummary(Array.isArray(summaryData) ? summaryData : (summaryData?.summary || []));

                // Problem detail: every ERROR record, every WARNING record, plus PROCESSED records
                // carrying a warning note.
                const errors = await fetchAll(syncId, taskId, { recordStatus: 'ERROR' });
                errors.forEach(r => { r._group = classifyError(r.comment); });
                // WARNING is a status in its own right — the record synced but the target rejected part
                // of it. Fetched by status rather than by comment phrase, so a new rejection code is in
                // the report the day it appears, without anyone adding a marker for it.
                const warnings = await fetchAll(syncId, taskId, { recordStatus: 'WARNING' });
                warnings.forEach(r => { r._group = classifyWarning(r.comment); });
                const warnLists = await Promise.all(
                    WARNING_MARKERS.map(async (mk) => {
                        const rs = await fetchAll(syncId, taskId, { errorMessage: mk.phrase });
                        return rs
                            .filter(r => (r.recordStatus || '').toUpperCase() !== 'ERROR') // errors already covered
                            .map(r => { r._group = { severity: mk.severity, key: mk.key, label: mk.label, guidance: mk.guidance }; return r; });
                    })
                );
                // De-dupe by uuid (a record can match more than one marker). Errors first, then the
                // WARNING-status records, then the comment-matched notes — so a record already grouped
                // by its status is never re-grouped by a phrase that happens to be in its comment.
                const seen = new Set([...errors, ...warnings].map(r => r.sprRecordUuid));
                const merged = [...errors, ...warnings];
                for (const list of warnLists) for (const r of list) {
                    if (!seen.has(r.sprRecordUuid)) { seen.add(r.sprRecordUuid); merged.push(r); }
                }
                if (!cancelled) { setProblemRecords(merged); setGeneratedAt(new Date()); }

                // Operator annotations for this task's report groups (best-effort).
                try {
                    const a = await syncService.getReportAnnotations(syncId, taskId);
                    if (!cancelled) {
                        const map = {};
                        for (const [k, v] of Object.entries(a?.annotations || {})) map[k] = v?.annotation || '';
                        setAnnotations(map);
                    }
                } catch (_) { /* annotations best-effort */ }
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to build report');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [syncId, taskId, scopeType, isSuperAdmin]);

    const counts = useMemo(() => {
        const c = { total: 0, processed: 0, warning: 0, error: 0, skipped: 0, inflight: 0 };
        for (const s of summary) {
            c.total += parseInt(s.totalRecords) || 0;
            c.processed += parseInt(s.processedRecords) || 0;
            // Records that reached the target but had part of them rejected. Counted separately from
            // processed (they need attention) AND from error (they do not need reprocessing).
            c.warning += parseInt(s.warningRecords) || 0;
            c.error += parseInt(s.errorRecords) || 0;
            c.skipped += parseInt(s.skippedRecords) || 0;
            c.inflight += parseInt(s.inFlightRecords) || 0;
        }
        return c;
    }, [summary]);

    const groupList = useMemo(() => {
        const groups = {};
        for (const r of problemRecords) {
            const g = r._group;
            if (!g) continue;
            if (!groups[g.key]) groups[g.key] = { meta: g, records: [] };
            groups[g.key].records.push(r);
        }
        const order = { error: 0, warning: 1, info: 2 };
        return Object.values(groups).sort((a, b) =>
            (order[a.meta.severity] - order[b.meta.severity]) || (b.records.length - a.records.length));
    }, [problemRecords]);

    const safeName = (projectName || taskName || 'sync').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    const csvFilename = `${safeName}-reservations-report.csv`;

    // ── Annotation editing ───────────────────────────────────────────────────────

    // ── Content builders (shared by download + email via ReportActions) ───────────
    const buildCsv = () => {
        const rows = [['group', 'severity', 'sourceId', 'targetId', 'status', 'category', 'annotation', 'comment', 'notes']];
        for (const r of problemRecords) {
            const g = r._group || {};
            rows.push([
                g.label || '', g.severity || '',
                r.sourcerecordid || '', r.targetRecordId || '', r.recordStatus || '',
                g.label || '', annotations[g.key] || '',
                (r.comment || '').replace(/\s+/g, ' ').trim(),
                notesFor(r).join(' | '),
            ]);
        }
        return rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    };

    const buildTextBody = () => {
        const lines = [`Reservations sync report — ${projectName || 'Sync project'}`];
        if (taskName) lines.push(`Task: ${taskName}${scopeType ? ` (${scopeType})` : ''}`);
        lines.push('');
        lines.push(`Total ${counts.total} · Processed ${counts.processed} · Warning ${counts.warning} · Error ${counts.error} · Skipped ${counts.skipped} · In flight ${counts.inflight}`);
        lines.push('');
        lines.push('Follow-up actions:');
        if (groupList.length === 0) lines.push('  None — every synced record is clean.');
        for (const g of groupList) {
            lines.push(`- [${g.meta.severity}] ${g.meta.label} — ${g.records.length} record(s)`);
            lines.push(`  ${g.meta.guidance}`);
            if (annotations[g.meta.key]) lines.push(`  Annotation: ${annotations[g.meta.key]}`);
            lines.push(`  Records: ${g.records.map(r => `${r.sourcerecordid || '?'}${r.targetRecordId ? `→${r.targetRecordId}` : ''}`).join(', ')}`);
        }
        return lines.join('\n');
    };

    const buildHtmlBody = (intro) => {
        const sevColor = { error: '#dc2626', warning: '#d97706', info: '#2563eb' };
        const stat = (n, l) => `<td style="text-align:center;padding:6px 14px"><div style="font-size:20px;font-weight:700;color:#111827">${n.toLocaleString()}</div><div style="font-size:11px;text-transform:uppercase;color:#6b7280">${l}</div></td>`;
        let groups = '';
        for (const g of groupList) {
            const col = sevColor[g.meta.severity] || '#6b7280';
            const ann = annotations[g.meta.key]
                ? `<div style="margin:8px 0 0;padding:8px 10px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;font-size:13px;color:#1e3a8a"><strong>Annotation:</strong> ${esc(annotations[g.meta.key])}</div>`
                : '';
            const ids = g.records.map(r => `${esc(r.sourcerecordid || '?')}${r.targetRecordId ? ` &rarr; ${esc(r.targetRecordId)}` : ''}`).join(', ');
            // Folded behind a disclosure triangle past the shared threshold, the same as the on-site
            // instructions email and the screens: a group of several hundred ids buries the group after
            // it, and the ids are for working through one group rather than for reading the list of them.
            const idBlock = htmlCollapsible(g.records.length,
                `${g.records.length} record${g.records.length === 1 ? '' : 's'} — show ids`,
                `<p style="font-size:12px;color:#6b7280;margin:8px 0 0;font-family:monospace;word-break:break-all">${ids}</p>`);
            groups += `<div style="border:1px solid #e5e7eb;border-left:4px solid ${col};border-radius:8px;padding:12px 14px;margin:0 0 10px;background:#fff">
                <div style="font-weight:600;color:#111827;font-size:15px">${esc(g.meta.label)} <span style="float:right;font-weight:700">${g.records.length}</span></div>
                <p style="font-size:13px;color:#374151;margin:6px 0 0">${esc(g.meta.guidance)}</p>
                ${ann}
                ${idBlock}
            </div>`;
        }
        return `<div style="font-family:Arial,Helvetica,sans-serif;color:#111827;max-width:720px;margin:0 auto">
            <h1 style="font-size:20px;margin:0 0 4px">${esc(projectName || 'Sync project')} — Reservations Sync Report</h1>
            <p style="color:#4b5563;font-size:14px;margin:0 0 12px">${esc(taskName || '')}${scopeType ? ` &middot; ${esc(scopeType)}` : ''}</p>
            ${intro ? `<p style="font-size:14px;color:#111827;white-space:pre-wrap;margin:0 0 14px">${esc(intro)}</p>` : ''}
            <table role="presentation" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;margin:0 0 16px"><tr>
                ${stat(counts.total, 'Total')}${stat(counts.processed, 'Processed')}${stat(counts.warning, 'Warning')}${stat(counts.error, 'Error')}${stat(counts.skipped, 'Skipped')}${stat(counts.inflight, 'In flight')}
            </tr></table>
            <h2 style="font-size:16px;color:#111827;border-bottom:1px solid #e5e7eb;padding-bottom:6px;margin:0 0 10px">Follow-up actions</h2>
            ${groups || '<p style="color:#6b7280;font-size:14px">No errors or flagged warnings — every synced record is clean.</p>'}
        </div>`;
    };

    if (!isSuperAdmin) {
        return (
            <div className="report-page">
                <div className="report-denied"><AlertCircle size={20} /><span>This report is available to superadmins only.</span></div>
            </div>
        );
    }

    return (
        <div className="report-page">
            <div className="report-toolbar report-no-print">
                <button className="report-back" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
                <ReportActions
                    syncId={syncId}
                    taskId={taskId}
                    disabled={loading || (!problemRecords.length && groupList.length === 0)}
                    noun="report"
                    defaultSubject={`[${projectName || 'Sync project'}] Reservations sync report`}
                    defaultIntro="Please see the reservations sync report below."
                    csvFilename={csvFilename}
                    buildCsv={buildCsv}
                    buildTextBody={buildTextBody}
                    buildHtmlBody={buildHtmlBody}
                />
            </div>

            {loading ? (
                <div className="report-loading"><Spinner /> Building report…</div>
            ) : error ? (
                <div className="report-denied"><AlertCircle size={20} /><span>{error}</span></div>
            ) : (
                <div className="report-sheet">
                    <header className="report-header">
                        <h1 className="report-title">{projectName || 'Sync Project'} — Reservations Sync Report</h1>
                        <div className="report-subtitle">
                            {taskName && <span>{taskName}</span>}
                            {scopeType && <span> &middot; {scopeType}</span>}
                        </div>
                        {generatedAt && <div className="report-generated">Generated {generatedAt.toLocaleString()}</div>}
                    </header>

                    <section className="report-summary">
                        <div className="report-stat report-stat-total">
                            <div className="report-stat-num">{counts.total.toLocaleString()}</div>
                            <div className="report-stat-label">Total synced</div>
                        </div>
                        <div className="report-stat report-stat-ok">
                            <div className="report-stat-num">{counts.processed.toLocaleString()}</div>
                            <div className="report-stat-label">Processed</div>
                        </div>
                        {/* Between Processed and Error deliberately: a warned record synced, so it
                            belongs on the successful side, but it still needs someone to look at it. */}
                        <div className="report-stat report-stat-warn">
                            <div className="report-stat-num">{counts.warning.toLocaleString()}</div>
                            <div className="report-stat-label">Warning</div>
                        </div>
                        <div className="report-stat report-stat-err">
                            <div className="report-stat-num">{counts.error.toLocaleString()}</div>
                            <div className="report-stat-label">Error</div>
                        </div>
                        <div className="report-stat report-stat-skip">
                            <div className="report-stat-num">{counts.skipped.toLocaleString()}</div>
                            <div className="report-stat-label">Skipped</div>
                        </div>
                        <div className="report-stat report-stat-pend">
                            <div className="report-stat-num">{counts.inflight.toLocaleString()}</div>
                            <div className="report-stat-label">In flight</div>
                        </div>
                    </section>

                    <section className="report-followups">
                        <h2 className="report-section-title">Follow-up actions</h2>
                        {groupList.length === 0 ? (
                            <p className="report-empty">No errors or flagged warnings — every synced record is clean.</p>
                        ) : (
                            groupList.map(group => {
                                const key = group.meta.key;
                                const annotation = annotations[key];
                                return (
                                    <div key={key} className={`report-group report-group-${group.meta.severity}`}>
                                        <div className="report-group-head">
                                            <span className={`report-badge report-badge-${group.meta.severity}`}>{group.meta.severity}</span>
                                            <span className="report-group-label">{group.meta.label}</span>
                                            <span className="report-group-count">{group.records.length}</span>
                                        </div>
                                        <p className="report-group-guidance">{group.meta.guidance}</p>

                                        {/* The same component the On-site report uses — one store, one
                                            key, one behaviour. */}
                                        <ReportAnnotation
                                            syncId={syncId}
                                            taskId={taskId}
                                            groupKey={key}
                                            value={annotation}
                                            onChange={(k, text) => setAnnotations(prev => {
                                                const next = { ...prev };
                                                if (text) next[k] = text; else delete next[k];
                                                return next;
                                            })}
                                            onError={setError}
                                        />

                                        <div className="report-idlist">
                                            {group.records.map(r => (
                                                <span key={r.sprRecordUuid || r.sourcerecordid} className="report-idpair">
                                                    {r.sourcerecordid || '?'}
                                                    <span className="report-arrow"> → </span>
                                                    {r.targetRecordId || '—'}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </section>

                    <section className="report-records">
                        <h2 className="report-section-title">Records needing attention ({problemRecords.length})</h2>
                        <div className="report-table-wrap">
                            <table className="report-table">
                                <thead>
                                    <tr><th>Source ID</th><th>Target ID</th><th>Status</th><th>Category</th><th>Notes</th></tr>
                                </thead>
                                <tbody>
                                    {problemRecords.map(r => {
                                        const g = r._group || {};
                                        const st = (r.recordStatus || '').toUpperCase();
                                        const notes = notesFor(r);
                                        return (
                                            <tr key={r.sprRecordUuid || r.sourcerecordid}>
                                                <td>{r.sourcerecordid || '—'}</td>
                                                <td>{r.targetRecordId || '—'}</td>
                                                <td><span className={`report-status report-status-${st.toLowerCase()}`}>{r.recordStatus || '—'}</span></td>
                                                <td>{g.label ? <span className={`report-cat report-cat-${g.severity}`}>{g.label}</span> : ''}</td>
                                                <td className="report-notes">
                                                    {notes.length === 0 ? '' : notes.map((n, i) => <div key={i}>{n}</div>)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
